import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Account, AccountType, AccountSubtype } from "../types";
import { SUBTYPES_BY_TYPE, SUBTYPE_LABELS } from "../types";

const accountFormSchema = z.object({
  code: z.string().min(1, "El código es requerido").max(20),
  name: z.string().min(1, "El nombre es requerido").max(200),
  accountType: z.enum(["ASSET", "LIABILITY", "EQUITY", "REVENUE", "EXPENSE"]),
  accountSubtype: z.enum([
    "CURRENT_ASSET",
    "FIXED_ASSET",
    "OTHER_ASSET",
    "CURRENT_LIABILITY",
    "LONG_TERM_LIABILITY",
    "OWNERS_EQUITY",
    "RETAINED_EARNINGS",
    "OPERATING_REVENUE",
    "OTHER_REVENUE",
    "COST_OF_GOODS_SOLD",
    "OPERATING_EXPENSE",
    "OTHER_EXPENSE",
    "TAX_EXPENSE",
  ]).nullable().optional(),
  normalBalance: z.enum(["DEBIT", "CREDIT"]),
  parentAccountId: z.string().nullable().optional(),
  isRootAccount: z.boolean(),
  isActive: z.boolean(),
  isHeader: z.boolean(),
  description: z.string().max(500).nullable().optional(),
});

export type AccountFormValues = z.infer<typeof accountFormSchema>;

interface AccountFormDrawerProps {
  /** Account to edit (null for create mode) */
  account: Account | null;
  /** Parent account when creating a sub-account via dropdown menu */
  parentAccount?: Account | null;
  /** All accounts for the parent selector */
  accounts: Account[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (values: AccountFormValues) => Promise<void>;
}

const accountTypeLabels: Record<string, string> = {
  ASSET: "Activo",
  LIABILITY: "Pasivo",
  EQUITY: "Patrimonio",
  REVENUE: "Ingreso",
  EXPENSE: "Gasto",
};

const normalBalanceLabels: Record<string, string> = {
  DEBIT: "Débito",
  CREDIT: "Crédito",
};

/**
 * Build a flat list of accounts with visual indentation for the parent selector.
 */
function buildAccountOptions(
  accounts: Account[],
  excludeId?: string
): { id: string; label: string; level: number }[] {
  // Build tree structure first
  type AccountWithChildren = Account & { children: AccountWithChildren[] };
  const accountMap = new Map<string, AccountWithChildren>();
  const roots: AccountWithChildren[] = [];

  // First pass: create nodes
  for (const account of accounts) {
    accountMap.set(account.id, { ...account, children: [] });
  }

  // Second pass: build hierarchy
  for (const account of accounts) {
    const node = accountMap.get(account.id)!;
    if (account.parentAccountId) {
      const parent = accountMap.get(account.parentAccountId);
      if (parent) {
        parent.children.push(node);
      } else {
        roots.push(node);
      }
    } else {
      roots.push(node);
    }
  }

  // Sort by code
  const sortByCode = (a: AccountWithChildren, b: AccountWithChildren) => a.code.localeCompare(b.code);
  roots.sort(sortByCode);

  // Flatten with indentation
  const options: { id: string; label: string; level: number }[] = [];

  function flatten(nodes: AccountWithChildren[], level: number) {
    for (const node of nodes) {
      // Exclude the account being edited (can't be its own parent)
      if (node.id !== excludeId) {
        const indent = "  ".repeat(level);
        options.push({
          id: node.id,
          label: `${indent}${node.code} - ${node.name}`,
          level,
        });
      }
      node.children.sort(sortByCode);
      flatten(node.children, level + 1);
    }
  }

  flatten(roots, 0);
  return options;
}

/**
 * Side drawer for creating/editing account details.
 *
 * Modes:
 * - Edit: account is provided
 * - Create with pre-selected parent: parentAccount is provided
 * - Create new: both null, user selects parent or creates root
 */
export function AccountFormDrawer({
  account,
  parentAccount,
  accounts,
  open,
  onOpenChange,
  onSave,
}: AccountFormDrawerProps) {
  const isEditMode = !!account;
  const isCreateChildMode = !account && !!parentAccount;

  // Determine if this is/should be a root account
  const isExistingRootAccount = account ? !account.parentAccountId : false;
  const defaultIsRoot = isCreateChildMode ? false : isExistingRootAccount;

  // For child accounts, inherit type, subtype, and balance from parent
  const defaultType = parentAccount?.accountType ?? account?.accountType ?? "ASSET";
  const defaultSubtype = parentAccount?.accountSubtype ?? account?.accountSubtype ?? null;
  const defaultBalance = parentAccount?.normalBalance ?? account?.normalBalance ?? "DEBIT";
  const defaultParentId = parentAccount?.id ?? account?.parentAccountId ?? null;

  const form = useForm<AccountFormValues>({
    resolver: zodResolver(accountFormSchema),
    defaultValues: {
      code: account?.code ?? "",
      name: account?.name ?? "",
      accountType: defaultType,
      accountSubtype: defaultSubtype,
      normalBalance: defaultBalance,
      parentAccountId: defaultParentId,
      isRootAccount: defaultIsRoot,
      isActive: account?.isActive ?? true,
      isHeader: account?.isHeader ?? false,
      description: account?.description ?? null,
    },
  });

  // Reset form when account/parentAccount changes
  useEffect(() => {
    if (open) {
      const newIsRoot = isCreateChildMode ? false : (account ? !account.parentAccountId : false);
      form.reset({
        code: account?.code ?? "",
        name: account?.name ?? "",
        accountType: parentAccount?.accountType ?? account?.accountType ?? "ASSET",
        accountSubtype: parentAccount?.accountSubtype ?? account?.accountSubtype ?? null,
        normalBalance: parentAccount?.normalBalance ?? account?.normalBalance ?? "DEBIT",
        parentAccountId: parentAccount?.id ?? account?.parentAccountId ?? null,
        isRootAccount: newIsRoot,
        isActive: account?.isActive ?? true,
        isHeader: account?.isHeader ?? false,
        description: account?.description ?? null,
      });
    }
  }, [open, account, parentAccount, isCreateChildMode, form]);

  // Watch form values for conditional rendering
  const isRootAccount = form.watch("isRootAccount");
  const selectedParentId = form.watch("parentAccountId");

  // Get parent account from selection
  const selectedParent = selectedParentId
    ? accounts.find((a) => a.id === selectedParentId)
    : null;

  // Auto-update type, subtype, and balance when parent changes
  useEffect(() => {
    if (!isRootAccount && selectedParent) {
      form.setValue("accountType", selectedParent.accountType);
      form.setValue("accountSubtype", selectedParent.accountSubtype);
      form.setValue("normalBalance", selectedParent.normalBalance);
    }
  }, [selectedParent, isRootAccount, form]);

  // When toggling to root, clear parent
  useEffect(() => {
    if (isRootAccount) {
      form.setValue("parentAccountId", null);
    }
  }, [isRootAccount, form]);

  const handleSubmit = async (values: AccountFormValues) => {
    await onSave(values);
  };

  const isSystemAccount = account?.isSystemAccount ?? false;

  // Build parent options (exclude current account when editing)
  const parentOptions = buildAccountOptions(accounts, account?.id);

  // Determine displayed type/subtype/balance (from parent if not root, or from form if root)
  const displayType = !isRootAccount && selectedParent
    ? selectedParent.accountType
    : form.watch("accountType");
  const displaySubtype = !isRootAccount && selectedParent
    ? selectedParent.accountSubtype
    : form.watch("accountSubtype");
  const displayBalance = !isRootAccount && selectedParent
    ? selectedParent.normalBalance
    : form.watch("normalBalance");

  // Get valid subtypes for the current account type
  const validSubtypes = SUBTYPES_BY_TYPE[displayType as AccountType] || [];

  // Clear subtype when account type changes (for root accounts)
  const watchedType = form.watch("accountType");
  useEffect(() => {
    if (isRootAccount) {
      const currentSubtype = form.getValues("accountSubtype");
      const validForType = SUBTYPES_BY_TYPE[watchedType as AccountType] || [];
      if (currentSubtype && !validForType.includes(currentSubtype as AccountSubtype)) {
        form.setValue("accountSubtype", null);
      }
    }
  }, [watchedType, isRootAccount, form]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>
            {isEditMode
              ? "Editar Cuenta"
              : isCreateChildMode
                ? "Nueva Subcuenta"
                : "Nueva Cuenta"}
          </SheetTitle>
          <SheetDescription>
            {isEditMode
              ? `Editando cuenta ${account.code} - ${account.name}`
              : isCreateChildMode && parentAccount
                ? `Creando subcuenta de ${parentAccount.code} - ${parentAccount.name}`
                : "Complete los datos de la nueva cuenta"}
          </SheetDescription>
        </SheetHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4 mt-6"
          >
            {/* Root Account Toggle - only show in create mode without pre-selected parent */}
            {!isEditMode && !isCreateChildMode && (
              <FormField
                control={form.control}
                name="isRootAccount"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-3 rounded-lg border p-3">
                    <FormControl>
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={field.onChange}
                        className="h-4 w-4 rounded border-gray-300"
                      />
                    </FormControl>
                    <div className="space-y-0.5">
                      <FormLabel className="!mt-0 font-medium">
                        Cuenta raíz
                      </FormLabel>
                      <FormDescription className="text-xs">
                        Las cuentas raíz no tienen cuenta padre
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />
            )}

            {/* Parent Account Selector - hidden when root or when pre-selected parent in create child mode */}
            {!isRootAccount && !isCreateChildMode && (
              <FormField
                control={form.control}
                name="parentAccountId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cuenta Padre</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value ?? ""}
                      disabled={isSystemAccount}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccione cuenta padre" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {parentOptions.map((option) => (
                          <SelectItem
                            key={option.id}
                            value={option.id}
                            className={cn(
                              "font-mono text-sm",
                              option.level > 0 && "pl-4"
                            )}
                          >
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Pre-selected parent indicator when creating child via dropdown */}
            {isCreateChildMode && parentAccount && (
              <div className="bg-muted p-3 rounded-md">
                <p className="text-sm text-muted-foreground">Cuenta padre</p>
                <p className="font-mono text-sm">
                  {parentAccount.code} - {parentAccount.name}
                </p>
              </div>
            )}

            {/* Code */}
            <FormField
              control={form.control}
              name="code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Código</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={
                        isCreateChildMode && parentAccount
                          ? `${parentAccount.code}XX`
                          : selectedParent
                            ? `${selectedParent.code}XX`
                            : "1"
                      }
                      disabled={isSystemAccount}
                      className="font-mono"
                    />
                  </FormControl>
                  <FormDescription>
                    {(isCreateChildMode && parentAccount) || selectedParent
                      ? `Código de subcuenta (ej: ${(parentAccount || selectedParent)?.code}01)`
                      : "Código único de la cuenta"}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Name */}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nombre</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Nombre de la cuenta" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Account Type - only editable for root accounts */}
            {isRootAccount ? (
              <FormField
                control={form.control}
                name="accountType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tipo de Cuenta</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                      disabled={isSystemAccount}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccione tipo" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.entries(accountTypeLabels).map(([value, label]) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : (
              <div className="space-y-1">
                <p className="text-sm font-medium">Tipo de Cuenta</p>
                <p className="text-sm text-muted-foreground bg-muted px-3 py-2 rounded-md">
                  {accountTypeLabels[displayType]} (heredado de cuenta padre)
                </p>
              </div>
            )}

            {/* Account Subtype - editable for root accounts, inherited for child accounts */}
            {isRootAccount ? (
              <FormField
                control={form.control}
                name="accountSubtype"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Clasificacion</FormLabel>
                    <Select
                      onValueChange={(value) => field.onChange(value || null)}
                      value={field.value ?? ""}
                      disabled={isSystemAccount}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccione clasificacion" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {validSubtypes.map((subtype) => (
                          <SelectItem key={subtype} value={subtype}>
                            {SUBTYPE_LABELS[subtype]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Clasificacion para agrupacion en reportes financieros
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : displaySubtype ? (
              <div className="space-y-1">
                <p className="text-sm font-medium">Clasificacion</p>
                <p className="text-sm text-muted-foreground bg-muted px-3 py-2 rounded-md">
                  {SUBTYPE_LABELS[displaySubtype as AccountSubtype]} (heredado de cuenta padre)
                </p>
              </div>
            ) : null}

            {/* Normal Balance - only editable for root accounts */}
            {isRootAccount ? (
              <FormField
                control={form.control}
                name="normalBalance"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Saldo Normal</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                      disabled={isSystemAccount}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccione saldo" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="DEBIT">Débito</SelectItem>
                        <SelectItem value="CREDIT">Crédito</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Indica si la cuenta aumenta con débito o crédito
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : (
              <div className="space-y-1">
                <p className="text-sm font-medium">Saldo Normal</p>
                <p className="text-sm text-muted-foreground bg-muted px-3 py-2 rounded-md">
                  {normalBalanceLabels[displayBalance]} (heredado de cuenta padre)
                </p>
              </div>
            )}

            {/* Description */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descripción</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      value={field.value ?? ""}
                      placeholder="Descripción opcional de la cuenta..."
                      rows={3}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Header Account */}
            <FormField
              control={form.control}
              name="isHeader"
              render={({ field }) => (
                <FormItem className="flex items-center gap-2">
                  <FormControl>
                    <input
                      type="checkbox"
                      checked={field.value}
                      onChange={field.onChange}
                      disabled={isSystemAccount}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                  </FormControl>
                  <div>
                    <FormLabel className="!mt-0">Cuenta de agrupación</FormLabel>
                    <FormDescription className="text-xs">
                      No permite transacciones directas, solo agrupa subcuentas
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />

            {/* Active Status */}
            <FormField
              control={form.control}
              name="isActive"
              render={({ field }) => (
                <FormItem className="flex items-center gap-2">
                  <FormControl>
                    <input
                      type="checkbox"
                      checked={field.value}
                      onChange={field.onChange}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                  </FormControl>
                  <FormLabel className="!mt-0">Cuenta activa</FormLabel>
                </FormItem>
              )}
            />

            {isSystemAccount && (
              <p className="text-sm text-muted-foreground bg-muted p-3 rounded-md">
                Esta es una cuenta de sistema. Algunos campos no pueden ser
                modificados.
              </p>
            )}

            {/* Actions */}
            <div className="flex gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={form.formState.isSubmitting}
                className="flex-1"
              >
                {form.formState.isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  "Guardar"
                )}
              </Button>
            </div>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}
